import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_LABELS = {
  queued: "Sırada",
  open: "Açık",
  in_progress: "Devam Ediyor",
  waiting_customer: "Müşteri Yanıtı Bekleniyor",
  resolved: "Çözüldü",
  closed: "Kapandı",
};

const STATUS_CLASS = {
  queued: "bg-indigo-100 text-indigo-700 border-indigo-200",
  open: "bg-sky-100 text-sky-700 border-sky-200",
  in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  waiting_customer: "bg-violet-100 text-violet-700 border-violet-200",
  resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-muted/60 text-foreground/80 border-border",
};

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function statusLabel(status) {
  return STATUS_LABELS[String(status || "").toLowerCase()] || "Açık";
}

function statusClass(status) {
  return STATUS_CLASS[String(status || "").toLowerCase()] || STATUS_CLASS.open;
}

export default function SupportTicketsPage() {
  const location = useLocation();
  const [supportModuleEnabled, setSupportModuleEnabled] = useState(true);
  const [supportModuleChecked, setSupportModuleChecked] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [ticketDetail, setTicketDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replySaving, setReplySaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [createTopic, setCreateTopic] = useState("entegrasyon");
  const [createMessage, setCreateMessage] = useState("");
  const [createSaving, setCreateSaving] = useState(false);

  const statusChips = [
    { key: "all", label: "Tümü" },
    { key: "queued", label: "Sırada" },
    { key: "open", label: "Açık" },
    { key: "in_progress", label: "Devam Ediyor" },
    { key: "waiting_customer", label: "Müşteri Bekleniyor" },
    { key: "resolved", label: "Çözüldü" },
    { key: "closed", label: "Kapandı" },
  ];

  const selectedTicket = useMemo(() => tickets.find((t) => t.id === selectedTicketId) || ticketDetail, [tickets, selectedTicketId, ticketDetail]);

  const loadTickets = async ({ keepSelection = true } = {}) => {
    if (!supportModuleEnabled) return;
    setLoading(true);
    try {
      const params = {
        page: 1,
        per_page: 100,
        status: statusFilter === "all" ? undefined : statusFilter,
      };
      const res = await api.get("/support/tickets", { params });
      const rows = Array.isArray(res?.items) ? res.items : [];
      setTickets(rows);

      if (!keepSelection) {
        const nextId = rows[0]?.id || "";
        setSelectedTicketId(nextId);
        return;
      }

      if (!selectedTicketId && rows[0]?.id) {
        setSelectedTicketId(rows[0].id);
      } else if (selectedTicketId && !rows.some((r) => r.id === selectedTicketId)) {
        setSelectedTicketId(rows[0]?.id || "");
      }
    } catch (err) {
      toast.error(err?.message || "Destek talepleri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    if (!supportModuleEnabled) return;
    if (!ticketId) {
      setTicketDetail(null);
      setMessages([]);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await api.get(`/support/tickets/${ticketId}`);
      setTicketDetail(res?.ticket || null);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, unread_from_support: false } : t)));
    } catch (err) {
      toast.error(err?.message || "Talep detayı yüklenemedi.");
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReply = async () => {
    if (!supportModuleEnabled) return;
    const text = String(replyMessage || "").trim();
    if (!selectedTicketId || text.length < 2) return;
    setReplySaving(true);
    try {
      await api.post(`/support/tickets/${selectedTicketId}/messages`, { message: text });
      setReplyMessage("");
      await Promise.all([loadTicketDetail(selectedTicketId), loadTickets()]);
      toast.success("Yanıtınız destek ekibine iletildi.");
    } catch (err) {
      toast.error(err?.message || "Yanıt gönderilemedi.");
    } finally {
      setReplySaving(false);
    }
  };

  const createTicket = async () => {
    const text = String(createMessage || "").trim();
    if (text.length < 8) {
      toast.error("Lütfen sorununuzu daha detaylı yazın.");
      return;
    }
    setCreateSaving(true);
    try {
      const res = await api.post("/support/tickets", {
        topic: createTopic,
        message: text,
        live_requested: false,
        source: "support_page",
      });
      setCreateOpen(false);
      setCreateMessage("");
      await loadTickets({ keepSelection: false });
      if (res?.ticket_id) setSelectedTicketId(res.ticket_id);
      toast.success(res?.message || "Yeni ticket oluşturuldu.");
    } catch (err) {
      toast.error(err?.message || "Ticket oluşturulamadı.");
    } finally {
      setCreateSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let res;
        try {
          res = await api.get("/integration-providers");
        } catch (primaryErr) {
          res = await api.get("/integrations/providers");
        }
        if (cancelled) return;
        const rows = Array.isArray(res?.items) ? res.items : [];
        setSupportModuleEnabled(rows.some((x) => String(x?.key || "").toLowerCase() === "support_center"));
      } catch {
        if (!cancelled) setSupportModuleEnabled(true);
      } finally {
        if (!cancelled) setSupportModuleChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!supportModuleChecked || !supportModuleEnabled) return;
    loadTickets({ keepSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, supportModuleChecked, supportModuleEnabled]);

  useEffect(() => {
    if (!supportModuleEnabled) return;
    loadTicketDetail(selectedTicketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId, supportModuleEnabled]);

  useEffect(() => {
    const ticketIdFromState = String(location?.state?.ticketId || "").trim();
    if (!ticketIdFromState) return;
    setSelectedTicketId(ticketIdFromState);
  }, [location?.state?.ticketId]);

  if (!supportModuleChecked) {
    return (
      <div className="rounded-2xl border border-border bg-card px-5 py-10 text-sm text-muted-foreground">
        Destek modülü kontrol ediliyor...
      </div>
    );
  }

  if (!supportModuleEnabled) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-8">
        <h2 className="text-base font-semibold text-amber-800">Destek modülü pasif</h2>
        <p className="mt-2 text-sm text-amber-700">
          Bu özellik super admin tarafından devre dışı bırakıldığı için destek talepleri görüntülenemiyor.
        </p>
      </div>
    );
  }

  return (
    <div className="page-shell space-y-5" data-testid="support-tickets-page">
      <div className="page-header mb-2">
        <div className="app-page-header">
          <h1 className="app-page-title">Destek Taleplerim</h1>
          <p className="app-page-subtitle">
            {tickets.length > 0 ? `${tickets.length} talep · ` : ""}Açtığınız talepleri buradan takip edin.
          </p>
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="h-9 rounded-lg bg-[#1487a3] px-4 text-sm font-semibold text-white hover:bg-[#12779b] transition-colors"
          >
            Yeni Ticket Aç
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {statusChips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={() => setStatusFilter(chip.key)}
            className={`h-8 rounded-full border px-3 text-xs font-medium transition ${
              statusFilter === chip.key
                ? "border-[#1487a3]/50 bg-[#1487a3]/10 text-[#1487a3] font-semibold"
                : "border-border bg-card text-muted-foreground hover:bg-muted/50"
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="border-b border-border/50 px-4 py-3 text-sm font-medium text-muted-foreground">
            Talepler ({tickets.length})
          </div>
          <div className="max-h-[68vh] overflow-y-auto">
            {loading ? (
              <div className="px-4 py-5 text-sm text-muted-foreground">Yükleniyor...</div>
            ) : tickets.length === 0 ? (
              <div className="px-4 py-5 text-sm text-muted-foreground">Henüz bir destek talebi yok.</div>
            ) : (
              tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className={`w-full border-b border-border/50 px-4 py-3 text-left transition ${
                    selectedTicketId === ticket.id ? "bg-sky-50" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate text-sm font-semibold text-foreground">{ticket.subject || "Destek Talebi"}</div>
                    {ticket.unread_from_support ? <span className="mt-0.5 h-2.5 w-2.5 rounded-full bg-rose-500" /> : null}
                  </div>
                  <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{ticket.last_message_preview || "-"}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className={`rounded-md border px-2 py-0.5 text-[11px] font-medium ${statusClass(ticket.status)}`}>
                      {statusLabel(ticket.status)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{ticket.ticket_code || `#${ticket.id?.slice(0, 8)}`}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          {!selectedTicketId ? (
            <div className="px-6 py-12 text-sm text-muted-foreground">Detay görmek için soldan bir destek kaydı seçin.</div>
          ) : detailLoading ? (
            <div className="px-6 py-12 text-sm text-muted-foreground">Talep detayları yükleniyor...</div>
          ) : (
            <>
              <div className="border-b border-border/50 px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className="text-base font-semibold text-foreground">{selectedTicket?.subject || "Destek Talebi"}</h2>
                    <p className="mt-1 text-xs text-muted-foreground">Talep No: {selectedTicket?.ticket_code || selectedTicket?.id}</p>
                  </div>
                  <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${statusClass(selectedTicket?.status)}`}>
                    {statusLabel(selectedTicket?.status)}
                  </span>
                </div>
              </div>

              <div className="max-h-[52vh] overflow-y-auto px-5 py-4 space-y-3 bg-muted/50/50">
                {messages.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Henüz mesaj yok.</div>
                ) : (
                  messages.map((msg) => {
                    const role = String(msg.sender_role || "");
                    const isUser = role === "user";
                    return (
                      <div key={msg.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                            isUser
                              ? "bg-sky-600 text-white"
                              : role === "system"
                              ? "bg-amber-50 text-amber-900 border border-amber-100"
                              : "bg-card text-slate-800 border border-border"
                          }`}
                        >
                          <div className={`text-[11px] ${isUser ? "text-sky-100" : "text-muted-foreground"}`}>{msg.sender_name || "Destek"}</div>
                          <div className="mt-1 whitespace-pre-wrap break-words">{msg.message}</div>
                          <div className={`mt-2 text-[10px] ${isUser ? "text-sky-100" : "text-muted-foreground"}`}>{formatDateTime(msg.created_at)}</div>
                        </div>
                      </div>
                    );
                  })
                )}
                {selectedTicket?.typing?.super ? (
                  <div className="text-xs text-muted-foreground">Destek ekibi yazıyor...</div>
                ) : null}
              </div>

              <div className="border-t border-border/50 px-5 py-4">
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Aynı talebe yanıt ver</label>
                <textarea
                  rows={4}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitReply();
                    }
                  }}
                  placeholder="Sorun devam ediyorsa buradan destek ekibine yazın..."
                  className="w-full resize-none rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={submitReply}
                    disabled={replySaving || replyMessage.trim().length < 2}
                    className="h-10 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                  >
                    {replySaving ? "Gönderiliyor..." : "Yanıt Gönder"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {createOpen ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 px-3">
          <div className="w-full max-w-xl rounded-2xl border border-border bg-card p-5 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Yeni Destek Talebi</h3>
              <button type="button" onClick={() => setCreateOpen(false)} className="text-sm text-muted-foreground hover:text-foreground/80">Kapat</button>
            </div>
            <div className="mt-4 grid gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Konu</label>
                <select
                  value={createTopic}
                  onChange={(e) => setCreateTopic(e.target.value)}
                  className="h-10 w-full rounded-lg border border-border px-3 text-sm text-foreground/80"
                >
                  <option value="entegrasyon">Entegrasyon</option>
                  <option value="fatura">Fatura / Muhasebe</option>
                  <option value="siparis">Sipariş / Operasyon</option>
                  <option value="teknik">Teknik Sorun</option>
                  <option value="oneris">Öneri / Geliştirme</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Sorun Detayı</label>
                <textarea
                  rows={5}
                  value={createMessage}
                  onChange={(e) => setCreateMessage(e.target.value)}
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  placeholder="Lütfen sorununuzu adım adım yazın..."
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="h-10 rounded-xl border border-border px-4 text-sm text-foreground/80">Vazgeç</button>
              <button type="button" onClick={createTicket} disabled={createSaving} className="h-10 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50">
                {createSaving ? "Oluşturuluyor..." : "Ticket Oluştur"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
